import React from "react";
import { Search, Download, Share2, Loader2, BookUser } from "lucide-react";
import { toPng } from "html-to-image";
import api from "../lib/api";
import { toast } from "sonner";
import { Reveal, useSEO } from "../components/site/Reveal";
import ReelDownloadButton from "../components/site/ReelDownloadButton";
import Newsletter from "../components/site/Newsletter";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const PAGE = 48;

export default function NamesDirectory() {
  useSEO(
    "Yorùbá Names A–Z (Orúkọ Yorùbá) — Meanings & Directory",
    "Browse a large A–Z directory of Yorùbá names (orúkọ). Search thousands of authentic Yorùbá names, explore their beauty, and share a name card."
  );

  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [letter, setLetter] = React.useState("A");
  const [term, setTerm] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [availableLetters, setAvailableLetters] = React.useState([]);

  const [shareTarget, setShareTarget] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const cardRef = React.useRef(null);

  React.useEffect(() => {
    api.get("/names/letters").then((r) => setAvailableLetters(r.data.map((x) => x.letter))).catch(() => {});
  }, []);

  const fetchPage = React.useCallback(async (opts) => {
    const { letter: l, q, skip } = opts;
    const params = new URLSearchParams();
    if (q && q.trim()) params.set("q", q.trim());
    else if (l) params.set("letter", l);
    params.set("skip", skip);
    params.set("limit", PAGE);
    const r = await api.get(`/names?${params.toString()}`);
    return r.data;
  }, []);

  // Reset + load when letter or search changes (debounced for search)
  React.useEffect(() => {
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await fetchPage({ letter, q: term, skip: 0 });
        if (!active) return;
        setItems(data.items);
        setTotal(data.total);
      } catch (e) {
        console.error("Failed to load names:", e);
        if (active) { setItems([]); setTotal(0); toast.error("Couldn't load names. Please try again."); }
      } finally {
        if (active) setLoading(false);
      }
    }, term ? 300 : 0);
    return () => { active = false; clearTimeout(t); };
  }, [letter, term, fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage({ letter, q: term, skip: items.length });
      setItems((prev) => [...prev, ...data.items]);
    } catch (e) {
      console.error("Failed to load more names:", e);
      toast.error("Couldn't load more names. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  };

  const generatePng = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#12100e" });
  };

  const withCard = async (n, action) => {
    setShareTarget(n);
    setBusy(`${action}-${n.id}`);
    await new Promise((res) => setTimeout(res, 80));
    try {
      const url = await generatePng();
      if (!url) return;
      if (action === "download") {
        const a = document.createElement("a");
        a.href = url; a.download = `${n.name}.png`; a.click();
      } else {
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], `${n.name}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: n.name, text: `${n.name} — a Yorùbá name · via ÀsÀNigHT Worldwide` });
        } else {
          const a = document.createElement("a");
          a.href = url; a.download = `${n.name}.png`; a.click();
        }
      }
    } catch (e) { console.warn("Name share card could not be generated or was cancelled:", e); } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-night text-cream min-h-screen">
      <header className="max-w-6xl mx-auto px-6 pt-24 pb-8">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <BookUser className="w-4 h-4 text-gold" />
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Yorùbá Names · Orúkọ</p>
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="names-title">
            The Names We Carry
          </h1>
          <p className="text-cream/70 text-lg leading-relaxed max-w-2xl">
            “Orúkọ ọmọ ni ń ro ọmọ” — a child's name shapes the child. Browse a growing
            A–Z directory of Yorùbá names and find the one that speaks to you.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <ReelDownloadButton section="names" format="landscape" label="Download Reel (16:9)" />
            <ReelDownloadButton section="names" format="vertical" label="Story Reel (9:16)" />
          </div>
        </Reveal>
      </header>

      {/* Search */}
      <div className="max-w-6xl mx-auto px-6 pb-6">
        <Reveal>
          <div className="relative">
            <Search className="w-4 h-4 text-cream/40 absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search a Yorùbá name…"
              data-testid="names-search-input"
              className="w-full rounded-full bg-night-surface border border-cream/20 pl-12 pr-6 py-3.5 text-cream placeholder:text-cream/40 focus:outline-none focus:border-gold transition-colors"
            />
          </div>
        </Reveal>
      </div>

      {/* A–Z jump bar */}
      <div className="max-w-6xl mx-auto px-6 pb-8">
        <div className="sticky top-20 z-20 flex flex-wrap gap-1.5 bg-night/85 backdrop-blur py-3 rounded-2xl" data-testid="names-az-bar">
          {ALPHABET.map((L) => {
            const enabled = availableLetters.includes(L);
            const active = letter === L && !term;
            return (
              <button
                key={L}
                disabled={!enabled}
                onClick={() => { setTerm(""); setLetter(L); }}
                data-testid={`names-letter-${L}`}
                className={`w-9 h-9 rounded-full text-sm font-semibold transition-colors ${
                  active ? "bg-gold text-night" :
                  enabled ? "text-cream/75 hover:bg-cream/10 hover:text-gold" :
                  "text-cream/20 cursor-not-allowed"
                }`}
              >
                {L}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-6 pb-24">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl text-gold">
            {term ? `Results for “${term}”` : `Names starting with ${letter}`}
          </h2>
          <span className="text-sm text-cream/45" data-testid="names-count">
            {total.toLocaleString()} {total === 1 ? "name" : "names"}
          </span>
        </div>

        {loading ? (
          <p className="text-cream/50 py-16 text-center">Loading names…</p>
        ) : items.length === 0 ? (
          <p className="text-cream/50 py-16 text-center" data-testid="names-empty">No names match your search.</p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((n) => (
                <div
                  key={n.id}
                  data-testid={`name-card-${n.id}`}
                  className="group flex items-center justify-between gap-3 border border-cream/12 rounded-xl bg-night-surface px-5 py-4 hover:border-gold/40 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-serif text-xl text-gold truncate">{n.name}</p>
                    {(n.gender || n.meaning) && (
                      <p className="text-xs text-cream/55 truncate">
                        {[n.gender, n.meaning].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1.5 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => withCard(n, "download")}
                      disabled={busy !== ""}
                      title="Download name card"
                      data-testid={`name-download-${n.id}`}
                      className="grid place-items-center w-9 h-9 rounded-full bg-gold/15 text-gold hover:bg-gold hover:text-night transition-colors disabled:opacity-40"
                    >
                      {busy === `download-${n.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => withCard(n, "share")}
                      disabled={busy !== ""}
                      title="Share name"
                      data-testid={`name-share-${n.id}`}
                      className="grid place-items-center w-9 h-9 rounded-full border border-cream/20 text-cream hover:border-gold hover:text-gold transition-colors disabled:opacity-40"
                    >
                      {busy === `share-${n.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {items.length < total && (
              <div className="text-center mt-10">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="names-load-more"
                  className="inline-flex items-center gap-2 border border-gold text-gold font-semibold px-8 py-3 rounded-full hover:bg-gold hover:text-night transition-colors disabled:opacity-50"
                >
                  {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Load more ({(total - items.length).toLocaleString()} left)
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Hidden branded share card */}
      {shareTarget && (
        <div aria-hidden="true" style={{ position: "fixed", top: 0, left: "-20000px", pointerEvents: "none" }}>
          <div
            ref={cardRef}
            data-testid="name-share-card"
            style={{
              width: 1080, height: 1080,
              background: "linear-gradient(155deg, #1b3327 0%, #12100e 55%)",
              color: "#F5EFE6", padding: 90, display: "flex", flexDirection: "column",
              justifyContent: "space-between", boxSizing: "border-box", position: "relative",
            }}
          >
            <div style={{ position: "absolute", inset: 40, border: "2px solid rgba(212,169,74,0.35)", borderRadius: 28, pointerEvents: "none" }} />
            <div style={{ position: "relative" }}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 46, color: "#D4A94A", lineHeight: 1 }}>ÀsÀNigHT</div>
              <div style={{ fontSize: 16, letterSpacing: 8, color: "rgba(245,239,230,0.6)", marginTop: 6 }}>WORLDWIDE</div>
              <div style={{ fontSize: 20, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginTop: 40 }}>Yorùbá Name · Orúkọ</div>
            </div>
            <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
              <div
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  color: "#D4A94A",
                  fontSize: shareTarget.name.length > 18 ? 96 : shareTarget.name.length > 12 ? 120 : 150,
                  lineHeight: 1.05, marginBottom: 24,
                }}
              >
                {shareTarget.name}
              </div>
              {shareTarget.gender && (
                <div style={{ fontSize: 30, letterSpacing: 2, textTransform: "uppercase", color: "rgba(245,239,230,0.55)", marginBottom: 14 }}>{shareTarget.gender}</div>
              )}
              {shareTarget.meaning && (
                <div style={{ fontSize: 34, lineHeight: 1.4, color: "#F5EFE6" }}>{shareTarget.meaning}</div>
              )}
            </div>
            <div style={{ position: "relative", borderTop: "1px solid rgba(245,239,230,0.15)", paddingTop: 28 }}>
              <div style={{ fontSize: 26, color: "#D4A94A", letterSpacing: 1 }}>asanightworldwide.com</div>
            </div>
          </div>
        </div>
      )}

      <Newsletter />
    </div>
  );
}
